import { Button, Checkbox } from "antd";
import type { CheckboxChangeEvent } from "antd/es/checkbox";
import styled from "styled-components";
import { CUSTOM_COLORS } from "@/shared/theme/colorUtils";
import { MOBILE_GUTTER, SAFE_AREA, mediaPhone } from "@/shared/theme/breakpoints";
import type { CreatorInvite } from "../../hooks/useCreatorInvitation";
import { Icon, type IconName } from "@/shared/components/Icon";
import type { LegalDocumentType } from "./LegalDocumentCard";
import { PAYMENT_TERMS_LABEL, SHOW_ADMINISTRATION_FEE } from "@/shared/legal/administrationFee";

const Card = styled.section`
	width: 720px;
	height: 720px;
	padding: 40px;
	border-radius: 24px;
	background: #fff;
	box-shadow: 0 20px 55px rgba(0, 57, 127, 0.22);
	display: flex;
	flex-direction: column;
	@media (max-width: 900px) {
		padding: 52px 32px;
		border-radius: 20px;
	}
	/* Phone: the card is the page; Continue sits at the bottom. */
	${mediaPhone`
		width: 100%;
		height: auto;
		min-height: 100vh;
		min-height: 100dvh;
		padding: calc(48px + ${SAFE_AREA.top}) calc(${MOBILE_GUTTER}px + ${SAFE_AREA.right})
			calc(32px + ${SAFE_AREA.bottom}) calc(${MOBILE_GUTTER}px + ${SAFE_AREA.left});
		border-radius: 0;
		box-shadow: none;
	`}
`;
const Wordmark = styled.img`
	display: block;
	width: 300px;
	height: 30px;
	max-width: 100%;
	object-fit: contain;
	margin: 0.5rem auto 1.75rem;
	${mediaPhone`
		width: 160px;
		height: 16px;
		margin-bottom: 1.5rem;
	`}
`;
const Intro = styled.p`
	margin: 0 0 33px;
	color: #555;
	font-size: 16px;
	line-height: 20px;
	text-align: center;
	${mediaPhone`
		margin-bottom: 24px;
		font-size: 14px;
	`}
`;
const Documents = styled.div`
	display: flex;
	flex-direction: column;
	gap: 16px;
`;
const DocumentButton = styled.button`
	width: 100%;
	min-height: 78px;
	padding: 14px 16px;
	border: 1px solid #ededed;
	border-radius: 9px;
	background: #fff;
	display: flex;
	align-items: center;
	gap: 11px;
	text-align: left;
	font: inherit;
	color: inherit;
	&[data-actionable="true"] {
		cursor: pointer;
	}
	&[data-actionable="true"]:hover,
	&[data-actionable="true"]:focus-visible {
		border-color: #c9d0ff;
		outline: none;
	}
	${mediaPhone`
		min-height: 82px;
		padding: 16px;
	`}
`;
const DocumentIcon = styled(Icon)`
	width: 24px;
	height: 24px;
	flex: 0 0 24px;
`;
const TermsIcon = styled.span`
	width: 24px;
	height: 28px;
	flex: 0 0 24px;
	position: relative;
	border: 2px solid #4964ee;
	border-radius: 5px;
	&::before,
	&::after {
		content: "";
		position: absolute;
		left: 4px;
		height: 2px;
		border-radius: 2px;
		background: #4964ee;
	}
	&::before {
		width: 12px;
		top: 11px;
	}
	&::after {
		width: 9px;
		top: 18px;
	}
`;
const DocumentCopy = styled.span`
	min-width: 0;
	display: flex;
	flex: 1;
	flex-direction: column;
	gap: 4px;
`;
const DocumentTitle = styled.strong`
	color: #303030;
	font-size: 0.95rem;
	line-height: 1.2;
	font-weight: 600;
`;
const DocumentDescription = styled.span`
	color: #a0a0a0;
	font-size: 12px;
	line-height: 1.3;
	font-weight: 500;
`;
const ExternalIcon = styled(Icon)`
	width: 24px;
	height: 24px;
	flex: 0 0 24px;
`;
const Consent = styled(Checkbox)`
	margin: 32px 16px 0;
	align-items: center;
	color: #303030;
	font-size: 0.875rem;
	line-height: 1.45;
	.ant-checkbox {
		transform: scale(1.4);
		margin-right: 10px;
	}
	.ant-checkbox-inner {
		border-color: #989898;
	}
	${mediaPhone`
		margin: 32px 8px 40px;
		align-items: flex-start;
		.ant-checkbox {
			transform: scale(1.25);
			margin: 4px 10px 0 0;
		}
	`}
`;
const ContinueButton = styled(Button)`
	height: 56px;
	margin-top: 60px;
	border: 0;
	border-radius: 28px;
	font-size: 0.9rem;
	font-weight: 600;
	box-shadow: none;
	&:not(:disabled) {
		background: ${CUSTOM_COLORS.PRIMARY};
	}
	&:disabled {
		color: #b2b2b2 !important;
		background: #d3d3d3 !important;
	}
	${mediaPhone`
		flex-shrink: 0;
		margin-top: auto;
	`}
`;
const HelpText = styled.p`
	margin: 11px 0 0;
	color: #747474;
	font-size: 0.875rem;
	line-height: 1.4;
	text-align: center;
`;

const legalCards = [
	{
		type: "terms_of_service",
		title: "Terms of Service",
		description: "Rules and guidelines for using our platform",
		icon: "terms",
	},
	{
		type: "fee_schedule",
		title: PAYMENT_TERMS_LABEL,
		description: SHOW_ADMINISTRATION_FEE ? "Pricing, fees, and invoicing" : "How and when you are paid",
		icon: "payment",
	},
	{
		type: "privacy_policy",
		title: "Privacy Policy",
		description: "How we collect, use, and protect your data",
		icon: "secure",
	},
] as const satisfies ReadonlyArray<{
	type: string;
	title: string;
	description: string;
	icon: IconName | "terms";
}>;

type LegalAcceptanceCardProps = {
	invite: CreatorInvite;
	accepted: boolean;
	submitting: boolean;
	onAcceptedChange: (accepted: boolean) => void;
	onContinue: () => void;
	onOpenDocument: (document: LegalDocumentType) => void;
};

export const LegalAcceptanceCard = ({
	invite,
	accepted,
	submitting,
	onAcceptedChange,
	onContinue,
	onOpenDocument,
}: LegalAcceptanceCardProps) => {
	const handleConsentChange = (event: CheckboxChangeEvent) => onAcceptedChange(event.target.checked);

	return (
		<Card>
			<Wordmark src="/Creatorverse_Wordmark_Gradient.svg" alt="Creatorverse" />
			<Intro>Please review and accept the following terms.</Intro>
			<Documents>
				{legalCards.map((card) => (
					<DocumentButton
						key={card.type}
						type="button"
						data-actionable="true"
						onClick={() => onOpenDocument(card.type)}
					>
						{card.icon === "terms" ? (
							<TermsIcon aria-hidden="true" />
						) : (
							<DocumentIcon name={card.icon} color="#4964ee" alt="" />
						)}
						<DocumentCopy>
							<DocumentTitle>{card.title}</DocumentTitle>
							<DocumentDescription>{card.description}</DocumentDescription>
						</DocumentCopy>
						<ExternalIcon name="newTab" alt="" />
					</DocumentButton>
				))}
			</Documents>
			<Consent checked={accepted} onChange={handleConsentChange}>
				I agree to the <strong>Terms of Service</strong>, <strong>{PAYMENT_TERMS_LABEL}</strong>, and{" "}
				<strong>Privacy Policy</strong>.
			</Consent>
			<ContinueButton type="primary" disabled={!accepted} loading={submitting} onClick={onContinue}>
				{invite.accountState === "new" ? "Continue Signing Up" : "Accept Invitation"}
			</ContinueButton>
			<HelpText>
				Terms of Service, {PAYMENT_TERMS_LABEL}, and Privacy Policy must be accepted to continue.
			</HelpText>
		</Card>
	);
};
